import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage, EventStats, TimelinePayload } from "@vzlive/shared";
import { apiFetch, apiUrl } from "../lib/api";

import { persistentId } from "../lib/visitor";

export type ChatStreamEvent =
  | { type: "chat.created"; message: ChatMessage }
  | { type: "chat.updated"; messageId: string; removed: boolean };

type ChatListener = (event: ChatStreamEvent) => void;

export function useTimeline(
  slug: string,
  { presence = true }: { presence?: boolean } = {},
) {
  const [timeline, setTimeline] = useState<TimelinePayload | null>(null);
  const [stats, setStats] = useState<EventStats | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "offline" | "error">(
    "loading",
  );
  const revision = useRef<string | undefined>(undefined);
  const timelineRef = useRef<TimelinePayload | null>(null);
  const chatListeners = useRef(new Set<ChatListener>());
  const dispatchChatEvent = useCallback((event: ChatStreamEvent) => {
    chatListeners.current.forEach((listener) => {
      try {
        listener(event);
      } catch {
        // Der Chat darf den SSE-Stream nie stören.
      }
    });
  }, []);
  const onChatEvent = useCallback((listener: ChatListener) => {
    chatListeners.current.add(listener);
    return () => {
      chatListeners.current.delete(listener);
    };
  }, []);

  const load = useCallback(
    async (quiet = false) => {
      try {
        const next = await apiFetch<TimelinePayload>(
          `/api/public/events/${encodeURIComponent(slug)}`,
        );
        if (next.revision !== revision.current) {
          revision.current = next.revision;
          timelineRef.current = next;
          setTimeline(next);
        }
        setState("ready");
        if (next.event.showLiveStats) {
          apiFetch<EventStats>(
            `/api/public/events/${encodeURIComponent(slug)}/stats`,
          )
            .then(setStats)
            .catch(() => setStats(null));
        }
      } catch {
        if (!quiet || !timelineRef.current)
          setState(navigator.onLine ? "error" : "offline");
      }
    },
    [slug],
  );

  useEffect(() => {
    void load();
    const visitor = presence
      ? persistentId("vzlive:visitor", () => localStorage)
      : null;
    const session = presence
      ? persistentId("vzlive:session", () => sessionStorage)
      : null;
    const url = new URL(
      apiUrl(`/api/public/events/${encodeURIComponent(slug)}/stream`),
    );
    if (visitor && session) {
      url.searchParams.set("visitor", visitor);
      url.searchParams.set("session", session);
    }
    const source = new EventSource(url, { withCredentials: true });
    let streamOpen = false;
    let lastReconciliation = Date.now();
    const refresh = () => void load(true);
    source.onopen = () => {
      streamOpen = true;
    };
    source.addEventListener("timeline.updated", refresh);
    source.addEventListener("event.updated", refresh);
    source.addEventListener("chat.created", (event) => {
      try {
        const parsed = JSON.parse((event as MessageEvent).data) as {
          payload?: { message?: ChatMessage };
        };
        if (parsed.payload?.message) {
          dispatchChatEvent({
            type: "chat.created",
            message: parsed.payload.message,
          });
        }
      } catch {
        // Malformed chat payloads are skipped, readers keep their state.
      }
    });
    source.addEventListener("chat.updated", (event) => {
      try {
        const parsed = JSON.parse((event as MessageEvent).data) as {
          payload?: { messageId?: string; removed?: boolean };
        };
        if (parsed.payload?.messageId && parsed.payload.removed) {
          dispatchChatEvent({
            type: "chat.updated",
            messageId: parsed.payload.messageId,
            removed: true,
          });
        }
      } catch {
        // Malformed chat payloads are skipped.
      }
    });
    source.addEventListener("presence.updated", (event) => {
      try {
        const message = JSON.parse((event as MessageEvent).data) as {
          payload?: { onlineNow?: number };
        };
        if (typeof message.payload?.onlineNow === "number") {
          setStats((current) =>
            current
              ? { ...current, onlineNow: message.payload!.onlineNow! }
              : current,
          );
        }
      } catch {
        // Ignore malformed SSE payloads and keep polling.
      }
    });
    source.onerror = () => {
      streamOpen = false;
      if (!navigator.onLine) setState("offline");
    };

    const polling = window.setInterval(() => {
      const reconciliationDue = Date.now() - lastReconciliation >= 60_000;
      if (
        document.visibilityState === "visible" &&
        (!streamOpen || reconciliationDue)
      ) {
        lastReconciliation = Date.now();
        void load(true);
      }
    }, 15_000);

    const heartbeat = async () => {
      if (!presence || !timelineRef.current?.event.id || !visitor || !session)
        return;
      try {
        const result = await apiFetch<{ onlineNow: number }>(
          "/api/public/heartbeat",
          {
            method: "POST",
            body: JSON.stringify({
              eventId: timelineRef.current.event.id,
              visitorId: visitor,
              sessionId: session,
            }),
          },
        );
        setStats((current) =>
          current ? { ...current, onlineNow: result.onlineNow } : current,
        );
      } catch {
        // Presence is best effort and must never interrupt the reader.
      }
    };
    // The SSE handshake records the initial presence. Periodic heartbeats only
    // keep it alive, avoiding two database writes when a reader first opens.
    const heartbeatTimer = window.setInterval(() => void heartbeat(), 45_000);

    return () => {
      source.close();
      window.clearInterval(polling);
      window.clearInterval(heartbeatTimer);
    };
  }, [dispatchChatEvent, load, presence, slug]);

  return { timeline, stats, state, reload: load, onChatEvent };
}
